import _ from 'lodash';
import { useLocation } from 'react-router-dom';
import { PaletteColorInput, PaletteInput } from 'src/components/pages/printType/__generated__/printTypeCreatePaletteMutation.graphql';
import config from './config';
import { PaletteType } from './enums';

export const pageWrapper = document.documentElement;
export const scrollSource = window;
export const isOldBackoffice = !!pageWrapper;

export type ValueOf<T> = T extends any[] ? T[number] : T[keyof T];

export type ReactType<T> = React.DetailedHTMLProps<React.InputHTMLAttributes<T>, T>;

export class T {
    public static _(message: string, domain: string): string {
        if (config.culture.startsWith('en')) return message;

        const wnd = window as any;
        return wnd.T2 ? wnd.T2._(message, domain) : message;
    }
}

export function isMobile() {
    return window.innerWidth < 768;
}

export function stringFormat(str: string, ...args: string[]): string {
    return args.reduce((prev, current, idx) => prev.replaceAll(`{${idx}}`, current), str);
}

// cutString
export function cutString(str: string, maxLength: number): string {
    if (str.length <= maxLength) {
        return str;
    }

    return str.substr(0, maxLength) + '...';
}

export function getCookie(name: string) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match?.[2];
}

export function setCookie(name: string, value: string, days: number) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; secure; samesite=strict`;
}

export function removeCookie(name: string) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure; samesite=strict`;
}

export function addDataLayer(data: any) {
    ((window as any).dataLayer || []).push(data);
}

export function getScroll(scrollElement: HTMLElement = pageWrapper) {
    const left = scrollElement.scrollLeft - (scrollElement.clientLeft || 0);
    const top = scrollElement.scrollTop - (scrollElement.clientTop || 0);
    const scrollHeight = scrollElement.scrollHeight;
    const totalHeight = scrollElement.clientHeight;
    const isAtEnd = top > scrollHeight - totalHeight - 10;

    return { top, left, scrollHeight, totalHeight, isAtEnd };
}

export function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

export function uuid() {
    var d = new Date().getTime();
    var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = ((d + Math.random() * 16) % 16) | 0;
        d = Math.floor(d / 16);
        return (c === 'x' ? r : (r & 0x7) | 0x8).toString(16);
    });
    return uuid;
}

export function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });
}

export const SUPPORTED_SCENE_FILE_UPLOAD_EXTENSIONS = '.babylon,.obj,.stl,.gltf,.glb,.dae,.3ds,.fbx,.x3d,.blend,.zip';

/**
 * Show a open file dialog
 *
 * @param onConfirm File selected callback
 * @param accept File accept pattern
 */
export function openFileDialog(onConfirm: (files: File[]) => void, accept: string = '.jpg,.png,.jpeg', multiple: boolean = false) {
    const input = document.createElement('input');
    input.style.display = 'none';
    input.setAttribute('type', 'file');
    input.setAttribute('accept', accept);
    if (multiple) input.setAttribute('multiple', 'multiple');

    input.addEventListener('change', (e) => {
        if (input.files && input.files.length > 0) {
            onConfirm(Array.from(input.files));
        }
        input.remove();
    });
    document.body.appendChild(input);
    input.click();

    setTimeout(() => input.remove(), 10000);
}

export function keysToCamelCase(object: T): T {
    if (_.isArray(object)) {
        return _.map(object, keysToCamelCase);
    }

    if (_.isObject(object)) {
        return _(object)
            .mapKeys((v, k) => _.camelCase(k))
            .value();
    }

    return object;
}

export function getUrlWithQueryParams(url: string) {
    const queryParams = window.location.search;
    const regex = new RegExp(/:[a-zA-Z]+\??\/?/g);
    url = url.replace(regex, '');
    return url + queryParams;
}

export function backofficeUrl(url: string) {
    return `/${config.culture}/Admin${url}`;
}

export function backofficeAnonymousUrl(url: string) {
    return `/${config.culture}${url}`;
}

export function isVtoPage() {
    return window.location.pathname.includes('virtual-try-on');
}

export function getJWTFromQueryParams() {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get('jwt');
}

export function assetUrl(url: string) {
    if (config.isInBackoffice) {
        return '/scripts/admin/_newBackoffice/assets/' + url;
    } else {
        return '/assets/' + url;
    }
}

export function routeMatchUrl(url: string) {
    return url.replace('/' + config.culture, '/:culture');
}

export function getPathName(url: string) {
    const parsedUrl = new URL(url);
    return `${parsedUrl.pathname}${parsedUrl.search}`;
}

export function getCulture() {
    let culture;

    const url = window.location.pathname;
    const match = url.match(/^\/[^/]+\/Admin/);
    const excludeAmin = url.match(/^\/([^/]+)(?=\/Admin|$)/);

    if (match && excludeAmin != null) {
        culture = excludeAmin[1];
    } else {
        if (getCookie('language') !== undefined) {
            culture = getCookie('language');
        } else {
            culture = 'en-US';
        }
    }

    return culture;
}

export async function moveToStorage(url: string) {
    var formData = new FormData();
    formData.append('url', url);

    const req = await fetch(config.apiUrl + 'images/storage', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json;charset=UTF-8',
            Authorization: 'Bearer ' + config.token
        },
        body: JSON.stringify({ url })
    });

    const res = await req.json();

    return res as { fileObjectID: number; url: string };
}

export function toLocalGuid(relayID: string) {
    const id = relayID.split('/').pop();
    return id ? id : null;
}

export function toLocalID(relayID: string | null | undefined) {
    if (!relayID) return null;

    const id = relayID.split('/').pop();
    return id ? parseInt(id) : null;
}

export function toGlobalID(id: number | string, type: string) {
    return `guid://Zakeke/${type}/${id}`;
}

export type GlobalID = `guid://Zakeke/${string}/${number}`;
export function isGlobalID(value: unknown): value is GlobalID {
    return typeof value === 'string' && /^guid:\/\/Zakeke\/\w+\/\w+$/.test(value);
}

export function getTypeFromGlobalID(relayID: string) {
    //return the type of the id: "guid://Zakeke/{type}/{id}"
    var result = /^guid:\/\/Zakeke\/\w+\/\w+$/.exec(relayID);
    if (!result) throw new Error(`Wrong id format ${relayID}`);

    const idSplitted = relayID.split('/');
    return idSplitted.at(3);
}

export function hexToRgb(hex: string) {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) throw new Error(`Can't convert ${hex} to rgb.`);

    return {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    };
}

export function hexToRgb01(hex: string) {
    const { r, g, b } = hexToRgb(hex);
    return { r: r / 255, g: g / 255, b: b / 255 };
}

export function hexToHSL(hex: string | null) {
    if (hex === null) return { h: 0, s: 0, l: 0 };
    // Convert hex to RGB first
    let { r, g, b } = hexToRgb(hex);

    var min,
        max,
        i,
        l,
        s,
        maxcolor,
        h,
        rgb = [];
    rgb[0] = r / 255;
    rgb[1] = g / 255;
    rgb[2] = b / 255;
    min = rgb[0];
    max = rgb[0];
    maxcolor = 0;
    for (i = 0; i < rgb.length - 1; i++) {
        if (rgb[i + 1] <= min) {
            min = rgb[i + 1];
        }
        if (rgb[i + 1] >= max) {
            max = rgb[i + 1];
            maxcolor = i + 1;
        }
    }
    if (maxcolor == 0) {
        h = (rgb[1] - rgb[2]) / (max - min);
    }
    if (maxcolor == 1) {
        h = 2 + (rgb[2] - rgb[0]) / (max - min);
    }
    if (maxcolor == 2) {
        h = 4 + (rgb[0] - rgb[1]) / (max - min);
    }
    if (h && isNaN(h)) {
        h = 0;
    }
    h = (h ? h : 0) * 60;
    if (h < 0) {
        h = h + 360;
    }
    l = (min + max) / 2;
    if (min == max) {
        s = 0;
    } else {
        if (l < 0.5) {
            s = (max - min) / (max + min);
        } else {
            s = (max - min) / (2 - max - min);
        }
    }
    s = s;
    return { h: h, s: s, l: l };
}

export function hslToHex(h: number, s: number, l: number) {
    let c = (1 - Math.abs(2 * l - 1)) * s,
        x = c * (1 - Math.abs(((h / 60) % 2) - 1)),
        m = l - c / 2,
        r = 0,
        g = 0,
        b = 0;

    if (0 <= h && h < 60) {
        r = c;
        g = x;
        b = 0;
    } else if (60 <= h && h < 120) {
        r = x;
        g = c;
        b = 0;
    } else if (120 <= h && h < 180) {
        r = 0;
        g = c;
        b = x;
    } else if (180 <= h && h < 240) {
        r = 0;
        g = x;
        b = c;
    } else if (240 <= h && h < 300) {
        r = x;
        g = 0;
        b = c;
    } else if (300 <= h && h < 360) {
        r = c;
        g = 0;
        b = x;
    }
    // Having obtained RGB, convert channels to hex
    let rString = Math.round((r + m) * 255).toString(16);
    let gString = Math.round((g + m) * 255).toString(16);
    let bString = Math.round((b + m) * 255).toString(16);

    // Prepend 0s, if necessary
    if (rString.length == 1) rString = '0' + rString;
    if (gString.length == 1) gString = '0' + gString;
    if (bString.length == 1) bString = '0' + bString;

    return '#' + rString + gString + bString;
}

export function isString(val: any): val is string {
    return typeof val === 'string';
}

export function getMessageFromGraphQLError(err: Error) {
    let message = 'Unexpected error.';

    if (Array.isArray(err) && err.length > 0) {
        message = err[0].message;
        if (err[0].extensions?.message) message = err[0].extensions?.message;
    } else {
        if (err && err.message) message = err.message;
    }

    return message;
}

export const validateAndFilterCurrentPaletteV1 = (palette: PaletteInput) => {
    const input = {
        ...palette,
        colors: palette.colors.filter((color) => !(color.name === '' && color.cmykHexCode === '' && color.rgbHexCode === '' && color.spotColorName === ''))
    };
    const { colors: nonEmptyColors } = input;

    const paletteNameIsEmpty = palette.name === '';
    const paletteColorsNamesAllMandatory = nonEmptyColors.every((color) => color.name !== '');
    const rgbHexCodeColorAllMAndatory = nonEmptyColors.every((color) => color.rgbHexCode !== '');
    const pantoneCodeColorAllOrNone =
        nonEmptyColors.every((color) => color.spotColorName === '') || nonEmptyColors.every((color) => color.spotColorName !== '');
    const cmykCodeColorAllOrNone = nonEmptyColors.every((color) => color.cmykHexCode === '') || nonEmptyColors.every((color) => color.cmykHexCode !== '');

    const getPaletteValidity = (paletteType: PaletteType) => {
        let isValid = !paletteNameIsEmpty && rgbHexCodeColorAllMAndatory && paletteColorsNamesAllMandatory;
        if (paletteType === PaletteType.CMYK) isValid = isValid && cmykCodeColorAllOrNone;
        if (paletteType === PaletteType.Pantone) isValid = isValid && pantoneCodeColorAllOrNone;
        return isValid;
    };

    return { isPaletteValid: getPaletteValidity(palette.paletteTypeID), input };
};

export const validateAndFilterCurrentPaletteV2 = (palette: PaletteInput, isEditing?: boolean) => {
    // Helper function to validate hex color format
    const isValidHexColor = (hex: string): boolean => {
        if (!hex || hex.trim() === '') return false;
        // Check if hex starts with #
        if (!hex.startsWith('#')) return false;
        // Remove # and validate the rest
        const cleanHex = hex.substring(1);
        // Check if it's a valid 3 or 6 character hex color
        return /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleanHex);
    };

    // Helper function to clean temporary IDs and GraphQL-specific fields from colors
    const cleanColors = (colors: PaletteColorInput[]) => {
        return colors.map((color) => {
            // Extract only the fields that should be sent to the server
            const cleanedColor: PaletteColorInput = {
                name: color.name || '',
                rgbHexCode: color.rgbHexCode || '',
                cmykHexCode: color.cmykHexCode || '',
                spotColorName: color.spotColorName || ''
            };

            // Only include the ID if it's not a temporary ID
            if (color.id && !color.id.startsWith('temp_')) {
                cleanedColor.id = color.id;
            }

            return cleanedColor;
        });
    };

    const input = !isEditing
        ? {
              ...palette,
              colors: [],
              addedColors: cleanColors(Array.from(palette.addedColors || [])),
              deletedColors: [],
              editedColors: []
          }
        : {
              ...palette,
              colors: [],
              addedColors: cleanColors(Array.from(palette.addedColors || [])),
              deletedColors: palette.deletedColors || [],
              editedColors: cleanColors(Array.from(palette.editedColors || []))
          };

    // Get all colors that will actually be processed (addedColors + editedColors)
    const allColorsToValidate = [...(input.addedColors || []), ...(input.editedColors || [])];

    // Validation function with detailed error messages
    const validatePalette = (paletteType: PaletteType): { isValid: boolean; errorMessage?: string } => {
        // 1. Check palette name
        if (!palette.name || palette.name.trim() === '') {
            return {
                isValid: false,
                errorMessage: 'Palette name is missing. Please enter a name for the palette to save it.'
            };
        }

        if (!isEditing && allColorsToValidate.length === 0) {
            return { isValid: false, errorMessage: 'Palette must contain at least one color to be saved.' };
        }

        // 2. Validate each color in the required order
        for (let i = 0; i < allColorsToValidate.length; i++) {
            const color = allColorsToValidate[i];
            const colorPosition = i + 1;

            // 2.1 Check Pantone color name (if palette type is Pantone)
            if (paletteType === PaletteType.Pantone) {
                if (!color.spotColorName || color.spotColorName.trim() === '') {
                    return {
                        isValid: false,
                        errorMessage: `Pantone color code is missing for color ${colorPosition}. Please enter a Pantone code for all colors in Pantone palettes.`
                    };
                }
            }

            // 2.2 Check color name
            if (!color.name || color.name.trim() === '') {
                return {
                    isValid: false,
                    errorMessage: `Color name is missing for color ${colorPosition}. Please enter a name for all colors.`
                };
            }

            // 2.3 Check RGB hex code
            if (!color.rgbHexCode || color.rgbHexCode.trim() === '') {
                return {
                    isValid: false,
                    errorMessage: `RGB hex code is missing for color "${color.name}". Please enter a valid hex color code.`
                };
            }

            // 2.4 Validate RGB hex code format
            if (!isValidHexColor(color.rgbHexCode)) {
                const missingHash = !color.rgbHexCode.startsWith('#');
                const errorDetail = missingHash ? 'Hex color code must start with "#"' : 'Invalid hex color format';
                return {
                    isValid: false,
                    errorMessage: `${errorDetail} for color "${color.name}". RGB hex code "${color.rgbHexCode}" is invalid. Please enter a valid hex color code (e.g., #FF0000 or #F00).`
                };
            }

            // 2.5 Check CMYK hex code (if palette type is CMYK)
            if (paletteType === PaletteType.CMYK) {
                if (!color.cmykHexCode || color.cmykHexCode.trim() === '') {
                    return {
                        isValid: false,
                        errorMessage: `CMYK color code is missing for color "${color.name}". Please enter CMYK values for all colors in CMYK palettes.`
                    };
                }
            }
        }

        return { isValid: true };
    };

    const validationResult = validatePalette(palette.paletteTypeID);

    return {
        isPaletteValid: validationResult.isValid,
        errorMessage: validationResult.errorMessage,
        input
    };
};

export const sanitizePaletteColor = (color: any, isForEdit?: boolean): PaletteColorInput => {
    const sanitizedColor: PaletteColorInput = {
        id: isForEdit ? color.id : null,
        name: color.name || '',
        cmykHexCode: color.cmykHexCode || '',
        spotColorName: color.spotColorName || '',
        rgbHexCode: color.rgbHexCode || ''
    };

    return sanitizedColor;
};

export const createEmptyPalette = (paletteTypeID?: number): PaletteInput => {
    return {
        name: '',
        colors: [],
        deletedColors: [],
        addedColors: [],
        editedColors: [],
        useConversionTable: true,
        paletteTypeID: paletteTypeID || PaletteType.Pantone,
        isZakekeDefault: false
    };
};

export const configurePalette = (palette: PaletteInput | null | undefined, colors?: PaletteColorInput[] | null): PaletteInput => {
    return {
        name: palette?.name || '',
        colors: colors || [],
        deletedColors: palette?.deletedColors || [],
        addedColors: palette?.addedColors || [],
        editedColors: palette?.editedColors || [],
        useConversionTable: palette?.useConversionTable || true,
        paletteTypeID: palette?.paletteTypeID || PaletteType.Pantone,
        isZakekeDefault: palette?.isZakekeDefault || false
    };
};

export const createFakePalette = (id: string, name: string, numberOfColors?: number): PaletteInput => {
    const palette: PaletteInput = {
        id: id,
        name: name,
        colors: [],
        useConversionTable: true,
        paletteTypeID: PaletteType.Pantone,
        isZakekeDefault: false
    };
    return palette;
};

export const adaptPaletteToPaletteInput = (palette: any): PaletteInput => {
    const { id, name, paletteType, isZakekeDefault } = palette;
    const paletteAdapted: PaletteInput = {
        id: id,
        name: name,
        colors: [],
        useConversionTable: true,
        paletteTypeID: paletteType,
        isZakekeDefault: isZakekeDefault
    };
    return paletteAdapted;
};

export function lightenDarkenColor(col: string, amt: number) {
    var usePound = false;
    if (col[0] == '#') {
        col = col.slice(1);
        usePound = true;
    }

    var num = parseInt(col, 16);

    var r = (num >> 16) + amt;

    if (r > 255) r = 255;
    else if (r < 0) r = 0;

    var b = ((num >> 8) & 0x00ff) + amt;

    if (b > 255) b = 255;
    else if (b < 0) b = 0;

    var g = (num & 0x0000ff) + amt;

    if (g > 255) g = 255;
    else if (g < 0) g = 0;

    return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16);
}

export function cartesianProduct(...a: any[]) {
    return a.reduce((a: any[], b: any[]) => a.flatMap((d) => b.map((e) => [d, e].flat())));
}

export async function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export function dataURLtoFile(dataurl: string, filename: string): File {
    var arr = dataurl.split(','),
        mime = (() => {
            const match = arr[0].match(/:(.*?);/);
            if (!match) {
                throw new Error('Invalid dataurl format');
            }
            return match[1];
        })(),
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
}

export async function urlToFile(url: string, filename: string): Promise<File> {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();
    const ext = blob.type.split('/')[1] || 'jpg';

    return new File([blob], `${filename}.${ext}`, { type: blob.type });
}

export async function uploadFile(file: File, isSideImageScalingEnabled?: boolean, anonymous?: boolean) {
    const formData = new FormData();
    formData.append('file', file);
    if (typeof isSideImageScalingEnabled === 'boolean') {
        formData.append('isSideImageScalingEnabled', isSideImageScalingEnabled.toString());
    }
    const endpoint = anonymous ? 'fileuploadanonymous/upload' : 'fileupload/upload';
    const response = await fetch(`${config.apiUrl}${endpoint}`, {
        method: 'POST',
        body: formData,
        headers: {
            Authorization: 'Bearer ' + config.token,
            Accept: 'application/json',
            'Accept-Language': config.culture
        }
    });
    const data = (await response.json()) as { url: string };
    return data.url;
}

export async function uploadImage(image: File | string) {
    const parsedFile =
        image instanceof File
            ? image
            : new File([image], 'icon.' + image.split('.').pop(), {
                  type: 'image/' + image.split('.').pop()
              });
    const formData = new FormData();
    formData.append('image', parsedFile);
    const response = await fetch(`${config.apiUrl}fileupload/upload`, {
        method: 'POST',
        body: formData,
        headers: {
            Authorization: 'Bearer ' + config.token,
            Accept: 'application/json',
            'Accept-Language': config.culture
        }
    });
    const data = (await response.json()) as { url: string };
    return data.url;
}

export async function loadScript(url: string) {
    // Download the globals_3d.js file by appending the script tag to the head
    const script3D = document.createElement('script');
    script3D.src = url;
    script3D.async = true;
    document.head.appendChild(script3D);

    // Wait for the globals_3d.js file to be loaded
    await new Promise((resolve) => {
        script3D.onload = resolve;
    });
}

/** COLORS */
export const componentToHex = (c: number) => {
    var hex = c.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
};

export const valueToColor = (value: string) => {
    let color = parseInt(value, 10);

    if (isNaN(color)) color = 0;

    if (color < 0) color = 0;

    if (color > 255) color = 255;

    return color;
};

export const clampValue = (value: string) => {
    let color = parseInt(value, 10);

    if (!isNaN(color) && color < 0) return '0';

    if (!isNaN(color) && color > 255) return '255';

    return value;
};

export const color3ToHex = (rgbColor: { r: number; g: number; b: number }): string => {
    return '#' + componentToHex(Math.round(rgbColor.r * 255)) + componentToHex(Math.round(rgbColor.g * 255)) + componentToHex(Math.round(rgbColor.b * 255));
};

export const rgbToHex = (rgbColor: { r: number; g: number; b: number }): string => {
    return '#' + componentToHex(Math.round(rgbColor.r)) + componentToHex(Math.round(rgbColor.g)) + componentToHex(Math.round(rgbColor.b));
};

/** BLOBS */
export function b64toBlob(b64Data: string, contentType: string, sliceSize?: number) {
    contentType = contentType || '';
    sliceSize = sliceSize || 512;

    var byteCharacters = atob(b64Data);
    var byteArrays = [];

    for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        var slice = byteCharacters.slice(offset, offset + sliceSize);

        var byteNumbers = new Array(slice.length);
        for (var i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        var byteArray = new Uint8Array(byteNumbers);

        byteArrays.push(byteArray);
    }

    var blob = new Blob(byteArrays, { type: contentType });
    return blob;
}

export async function blobTob64(blob: Blob) {
    return new Promise<string>((resolve) => {
        var a = new FileReader();
        a.onload = function (e) {
            resolve((e.target as FileReader).result as string);
        };
        a.readAsDataURL(blob);
    });
}

// Convert a 3 digit hex color to 6 digit hex color (e.g #333 to #333333)
export function expandHexColor(hex: string) {
    if (hex.length === 4) {
        return '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
    }
    return hex;
}
// map(x => x) is for converting backbone objects in normal array
export const ascComparator = <T>(getProp: (item: T) => string | number) => {
    return (a: T, b: T) => {
        let propA = getProp(a);
        let propB = getProp(b);

        if (propA < propB) return -1;
        if (propB < propA) return 1;
        return 0;
    };
};

// Sanitize returnUrl: allow only same-origin, no javascript: or data: schemes
export const isSafeReturnUrl = (url: string) => {
    try {
        // Disallow javascript: and data: schemes
        if (/^(javascript|data):/i.test(url.trim())) return false;
        // Only allow relative URLs or same-origin absolute URLs
        const parsed = new URL(url, window.location.origin);
        console.log('Parsed URL:', parsed);
        return parsed.origin === window.location.origin;
    } catch {
        return false;
    }
};

export const sanitizeInput = (
    input: string,
    options?: {
        allowHtml?: boolean;
        allowedTags?: string[];
    }
): string => {
    if (!input) return '';

    // If HTML is not allowed, encode all HTML entities
    if (!options?.allowHtml) {
        return input
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#x27;')
            .replace(/\//g, '&#x2F;');
    }

    // If HTML is allowed but with restricted tags
    const allowedTags = options.allowedTags || ['b', 'i', 'em', 'strong', 'u'];
    const allowedTagsRegex = allowedTags.join('|');

    // Remove script tags and event handlers
    let sanitized = input
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
        .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
        .replace(/on\w+\s*=\s*[^\s>]*/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/data:text\/html/gi, '');

    // Remove all tags except allowed ones
    sanitized = sanitized.replace(new RegExp(`<(?!\\/?(${allowedTagsRegex})\\b)[^>]*>`, 'gi'), '');

    return sanitized;
};

export const validateInputSecurity = (
    input: string,
    options?: {
        allowHtml?: boolean;
        strictMode?: boolean;
    }
): {
    isValid: boolean;
    invalidCharacters: string[];
    dangerousPatterns: string[];
    message?: string;
} => {
    if (!input) {
        return { isValid: true, invalidCharacters: [], dangerousPatterns: [] };
    }

    const invalidChars = new Set<string>();
    const dangerousPatterns: string[] = [];

    // Check for HTML/XML special characters (if HTML not allowed)
    if (options?.allowHtml == false) {
        const htmlChars = ['<', '>'];
        htmlChars.forEach((char) => {
            if (input.includes(char)) {
                invalidChars.add(char);
            }
        });
    }

    // Check for script tags
    if (/<script/i.test(input)) {
        dangerousPatterns.push('<script>');
        const matches = input.match(/<script[^>]*>/gi);
        matches?.forEach((match) => invalidChars.add(match));
    }

    // Check for iframe tags
    if (/<iframe/i.test(input)) {
        dangerousPatterns.push('<iframe>');
        const matches = input.match(/<iframe[^>]*>/gi);
        matches?.forEach((match) => invalidChars.add(match));
    }

    // Check for event handlers (with quotes)
    const eventHandlersWithQuotes = input.match(/on\w+\s*=\s*["'][^"']*["']/gi) || [];
    // Check for event handlers (without quotes)
    const eventHandlersWithoutQuotes = input.match(/on\w+\s*=\s*[^\s>"'][^\s>]*/gi) || [];

    const allEventHandlers = [...eventHandlersWithQuotes, ...eventHandlersWithoutQuotes];
    if (allEventHandlers.length > 0) {
        dangerousPatterns.push('event handlers (onclick, onload, etc.)');
        allEventHandlers.forEach((handler) => invalidChars.add(handler));
    }

    // Check for javascript: protocol
    if (/javascript:/i.test(input)) {
        dangerousPatterns.push('javascript: protocol');
        const matches = input.match(/javascript:[^"'\s]*/gi);
        matches?.forEach((match) => invalidChars.add(match));
    }

    // Check for data: protocol with HTML
    if (/data:text\/html/i.test(input)) {
        dangerousPatterns.push('data:text/html protocol');
        invalidChars.add('data:text/html');
    }

    // Check for vbscript: protocol
    if (/vbscript:/i.test(input)) {
        dangerousPatterns.push('vbscript: protocol');
        const matches = input.match(/vbscript:[^"'\s]*/gi);
        matches?.forEach((match) => invalidChars.add(match));
    }

    // Strict mode: check for quotes in suspicious contexts
    if (options?.strictMode) {
        // Check for quotes that might break out of attributes
        const suspiciousQuotes = input.match(/["'](?=.*[<>])/g);
        if (suspiciousQuotes && (input.includes('<') || input.includes('>'))) {
            dangerousPatterns.push('quotes combined with HTML characters');
        }
    }

    // Check for null bytes
    if (input.includes('\0')) {
        invalidChars.add('\\0 (null byte)');
        dangerousPatterns.push('null bytes');
    }

    const invalidCharArray = Array.from(invalidChars);
    const isValid = invalidCharArray.length === 0 && dangerousPatterns.length === 0;

    let message: string | undefined;
    if (!isValid) {
        const parts: string[] = [];
        if (dangerousPatterns.length > 0) {
            parts.push(`Dangerous patterns detected: ${dangerousPatterns.join(', ')}`);
        }
        if (invalidCharArray.length > 0) {
            parts.push(`Invalid characters detected: ${invalidCharArray.join(', ')}`);
        }
        message = parts.join('. ');
    }

    return {
        isValid,
        invalidCharacters: invalidCharArray,
        dangerousPatterns,
        message
    };
};

export const base64ToFile = (base64String: string, fileName: string): File => {
    const arr = base64String.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) {
        throw new Error('Invalid base64 string');
    }

    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], fileName, { type: mime });
};

export const getNextRuleNumber = (ruleNames: readonly (string | null | undefined)[]) => {
    const numbers = ruleNames.map((name) => {
        const match = name?.match(/^Rule (\d+)$/);
        return match ? parseInt(match[1]) : 0;
    });
    return Math.max(0, ...numbers) + 1;
};
